/* Las medidas van en custom properties locales (`--_h`, `--_px`…) para que
   cada tamaño sea una sola regla y no tres. Los estados son CSS puro: el
   hover no pasa por React, y `:focus-visible` funciona con teclado.
   Píldora siempre: sm 32 · md 40 · lg 48. */
.button {
  --_h: var(--h-control-md);
  --_px: var(--s-8);
  --_fs: var(--fs-xs);
  --_gap: var(--s-4);

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--_gap);
  height: var(--_h);
  padding: 0 var(--_px);
  font-family: var(--font-body);
  font-size: var(--_fs);
  font-weight: var(--fw-semibold);
  line-height: 1;
  border-radius: var(--r-control);
  border: var(--bw-hair) solid transparent;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transition:
    background var(--dur-fast) var(--ease-standard),
    box-shadow var(--dur-fast) var(--ease-standard),
    transform var(--dur-fast) var(--ease-standard),
    color var(--dur-fast) var(--ease-standard),
    border-color var(--dur-fast) var(--ease-standard);
}

.button[data-size="sm"] {
  --_h: var(--h-control-sm);
  --_px: var(--s-6);
  --_fs: var(--fs-2xs);
  --_gap: var(--s-3);
}
.button[data-size="lg"] {
  --_h: var(--h-control-lg);
  --_px: var(--s-9);
  --_fs: var(--fs-md);
  --_gap: var(--s-5);
}
.button[data-full-width] {
  display: flex;
  width: 100%;
}

/* Variantes. El `:hover` sólo se aplica donde hay puntero: en un celular un
   hover que se pega después del tap es un estado que miente. */
.button[data-variant="primary"] {
  background: var(--action-primary);
  border-color: var(--action-primary);
  color: var(--action-primary-text);
}
.button[data-variant="secondary"] {
  background: var(--surface-card);
  border-color: var(--border-brand);
  color: var(--text-brand);
}
.button[data-variant="soft"] {
  background: var(--surface-brand-faint);
  border-color: var(--surface-brand-faint);
  color: var(--text-brand);
}
.button[data-variant="ghost"] {
  --_px: var(--s-6);
  background: transparent;
  color: var(--text-strong);
}
.button[data-variant="outline"] {
  background: transparent;
  border-color: var(--border-strong);
  color: var(--text-strong);
}
.button[data-variant="accent"] {
  background: var(--action-accent);
  border-color: var(--action-accent);
  color: var(--action-accent-text);
}
.button[data-variant="inverse"] {
  background: var(--action-inverse);
  border-color: var(--action-inverse);
  color: var(--action-inverse-text);
}
.button[data-variant="ink"] {
  background: var(--action-ink);
  border-color: var(--action-ink);
  color: var(--brand-white);
}
.button[data-variant="danger"] {
  background: var(--action-danger);
  border-color: var(--action-danger);
  color: var(--brand-white);
}
/* Destructiva pero no la principal de la fila: blanca con borde y texto rojos. La confirmación va después. */
.button[data-variant="danger-soft"] {
  background: var(--surface-card);
  border-color: var(--status-danger-border);
  color: var(--text-danger);
}

@media (hover: hover) {
  .button[data-variant="primary"]:hover:enabled,
  .button[data-variant="primary"]:hover:not([aria-disabled]) {
    background: var(--action-primary-hover);
    border-color: var(--action-primary-hover);
  }
  .button[data-variant="secondary"]:hover:enabled,
  .button[data-variant="secondary"]:hover:not([aria-disabled]) {
    background: var(--action-secondary-hover);
  }
  .button[data-variant="soft"]:hover:enabled,
  .button[data-variant="soft"]:hover:not([aria-disabled]) {
    background: var(--surface-brand-soft);
    border-color: var(--surface-brand-soft);
  }
  .button[data-variant="ghost"]:hover:enabled,
  .button[data-variant="ghost"]:hover:not([aria-disabled]) {
    color: var(--text-brand);
  }
  .button[data-variant="outline"]:hover:enabled,
  .button[data-variant="outline"]:hover:not([aria-disabled]) {
    border-color: var(--border-hover);
    background: var(--action-quiet-hover);
  }
  .button[data-variant="accent"]:hover:enabled,
  .button[data-variant="accent"]:hover:not([aria-disabled]) {
    background: var(--action-accent-hover);
    border-color: var(--action-accent-hover);
  }
  .button[data-variant="inverse"]:hover:enabled,
  .button[data-variant="inverse"]:hover:not([aria-disabled]) {
    background: var(--action-inverse-hover);
    border-color: var(--action-inverse-hover);
  }
  .button[data-variant="ink"]:hover:enabled,
  .button[data-variant="ink"]:hover:not([aria-disabled]) {
    background: var(--action-ink-hover);
    border-color: var(--action-ink-hover);
  }
  .button[data-variant="danger-soft"]:hover:enabled,
  .button[data-variant="danger-soft"]:hover:not([aria-disabled]) {
    background: var(--status-danger-bg);
  }
  .button[data-variant="danger"]:hover:enabled,
  .button[data-variant="danger"]:hover:not([aria-disabled]) {
    background: var(--action-danger-hover);
    border-color: var(--action-danger-hover);
  }
}

/* Press: un asentamiento, no un rebote (sin ripples, sin bounce). */
.button:active:enabled,
.button:active:not([aria-disabled]) {
  transform: scale(0.98);
}
.button[data-variant="primary"]:active:enabled {
  background: var(--action-primary-active);
  border-color: var(--action-primary-active);
}
.button[data-variant="ghost"]:active:enabled,
.button[data-variant="outline"]:active:enabled {
  background: var(--action-quiet-active);
}

.button:focus-visible {
  outline: none;
  box-shadow: var(--ring-focus);
}

.button:disabled,
.button[aria-disabled="true"] {
  background: var(--action-disabled);
  color: var(--action-disabled-text);
  border-color: transparent;
  opacity: var(--action-disabled-opacity);
  box-shadow: none;
  cursor: not-allowed;
  transform: none;
}
/* Un ghost deshabilitado no gana fondo: se apaga el texto y listo (el "Gestionar" de la fila propia del equipo). */
.button[data-variant="ghost"]:disabled,
.button[data-variant="ghost"][aria-disabled="true"] {
  background: transparent;
  color: var(--text-subtle);
  opacity: 1;
}
.button[aria-busy="true"] {
  cursor: progress;
}

.spinner {
  animation: girar 800ms linear infinite;
}
@keyframes girar {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .button {
    transition: none;
  }
  /* El spinner sigue girando porque es información (la acción está en curso),
     pero más lento. */
  .spinner {
    animation-duration: 1.6s;
  }
}
